import React, { useCallback, useState, memo, useEffect } from 'react';
import { styled, keyframes } from '@compiled/react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';

import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { usePubSubEvent } from '@confluence/pubsub-client';
import { COVERPICTUREWIDTH } from '@confluence/content-topper';
import {
	MIN_DYNAMIC_COVER_IMAGE_HEIGHT,
	COVER_IMAGE_FIXED_HEIGHT,
	getDynamicCoverImageHeight,
	COVER_IMAGE_FIXED_WIDTH,
} from '@confluence/page-cover-picture';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { useEditorWidthObserver } from '@confluence/editor-features';

import { CoverPictureAndButton } from './CoverPictureAndButton';
import { EmojiButton } from './EmojiButton';
import { ContentStateHoverButton } from './ContentStateHoverButton';
import { AlignmentButtonContainer } from './AlignmentButtonContainer';
import type {
	FloatingToolbarButtonStyleType,
	ToolbarAboveTitleProps,
} from './titleareaContentComponentsTypes';
import { SuggestTitleButton } from './SuggestTitleButton';
import { AISuggestedImageHeaderLoading } from './AISuggestedImageHeaderLoading';
import { AlignmentComponent } from './AlignmentComponent';
import { ContentStateUpdatedSubscription } from './ContentStateUpdatedSubscription';
import { TOOLBAR_OPACITY_VAR } from './EditorTitleWithTitleAreaComponents';

const toolbarAboveTitleBoxStylesBase = xcss({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	marginBottom: 'space.050',
});

const toolbarAboveTitleLeftAlignmentStyles = xcss({
	alignItems: 'start',
});

const toolbarAboveTitleCenterAlignmentStyles = xcss({
	alignItems: 'center',
	marginBottom: 'space.050',
});

const toolbarAboveTitleBottomSpacingStyle = xcss({
	marginBottom: 'space.200',
});

const coverPictureAndButtonWithCoverPictureBoxStyles = xcss({
	width: '100%',
	marginBottom: 'space.150',
});

const coverPictureAndButtonWithCoverPictureBoxStylesNew = xcss({
	width: '100%',
	marginBottom: 'space.0',
});

const toolbarStyle = xcss({
	// @ts-ignore TODO: refactor to align this to the design system
	marginTop: '-22px',
});

const toolbarPlaceholderBoxStylesMarginBottomSpace0 = xcss({
	marginBottom: 'space.0',
});

const contentJumpMitigation_toolbarPlaceholderBoxStylesMarginBottomSpace36px = xcss({
	// This is a temp non token size value to match a height of a structure which
	// is built using multiple components, with sizes that add up to 36px.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	marginBottom: '36px' as any,
});

const toolbarPlaceholderBoxStylesMarginBottomSpace500 = xcss({
	marginBottom: 'space.500',
});

const toolbarPlaceholderWithEmojiMarginTopStyle = xcss({
	marginTop: 'space.600',
});

const toolbarPlaceholderMarginTopStyle = xcss({
	marginTop: 'space.300',
});

const editorAppearanceButtonBoxXcss = xcss({
	marginRight: 'space.025',
});

const editorAppearanceButtonLivePageXcss = xcss({
	marginLeft: 'space.0',
});

const editorAppearanceButtonClassicPageXcss = xcss({
	marginLeft: 'space.negative.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalLineWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalLine = styled.span({
	width: '24px',
	height: '0px',
	transform: 'rotate(90deg)',
	border: `0.5px solid ${token('color.border')}`,
});

const fade = keyframes({
	from: {
		opacity: '0%',
	},
	to: {
		opacity: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButtonsWrapper = styled.div<{
	isUploadingPicture: boolean;
	hasCoverPicture: boolean;
	shouldShowToolbar: boolean;
	isTitleCenterAligned?: boolean;
	hasEmoji?: boolean;
}>({
	background: token('elevation.surface'),
	position: 'relative',
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignSelf: ({ hasCoverPicture, isUploadingPicture, isTitleCenterAligned }) =>
		(hasCoverPicture || isUploadingPicture) && isTitleCenterAligned ? 'center' : 'initial',
	width: 'fit-content',
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	padding: () =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('live_pages_content_jump_mitigation') ? token('space.025') : token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	boxShadow: () =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		!fg('confluence_frontend_title_hover_menu') && token('elevation.shadow.overlay'),
	zIndex: 200,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	display: ({ shouldShowToolbar }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('confluence_title_toolbar_ttvc_fix') ? 'flex' : shouldShowToolbar ? 'flex' : 'none',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ shouldShowToolbar }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('confluence_title_toolbar_ttvc_fix')
			? shouldShowToolbar
				? 1
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					`var(${TOOLBAR_OPACITY_VAR})`
			: 1,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values
	transition: () => fg('confluence_title_toolbar_ttvc_fix') && 'opacity 0.1s ease-in',

	// This mirrors the stylistic choice found in `platform/packages/editor/editor-common/src/styles/shared/plugins.ts`
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not([disabled])::after': {
			border: 'none', // remove blue border when an item has been selected
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	...(fg('confluence_frontend_title_hover_menu') && {
		animationName: fade,
		animationDuration: '0.1s',
		animationTimingFunction: 'ease-in',
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	columnGap: fg('confluence_frontend_title_hover_menu') ? token('space.050') : token('space.0'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginTop: ({ hasCoverPicture, hasEmoji }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('confluence_title_toolbar_ttvc_fix') &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		(fg('confluence_frontend_title_hover_menu') && hasCoverPicture
			? hasEmoji
				? token('space.600')
				: token('space.300')
			: undefined),
});

const floatingToolbarButtonStyle: FloatingToolbarButtonStyleType = {
	height: '32px',
	alignItems: 'center',
};

export const ToolbarAboveTitle = memo(
	({
		contentId,
		userOffline,
		isUnpublishedDraft,
		isTitleDisabled,
		isTitleHovered,
		isCoverPictureModalOpen,
		hasCoverPicture,
		isUploadingPicture,
		isAddHeaderImageButtonFocused,
		setSelectedEmoji,
		onEmojiPickerLoaded,
		onUpdateStartEmoji,
		onUpdateEndEmoji,
		isEmojiButtonFocused,
		hasEmoji,
		contentType,
		isPublishingContent,
		hasContentState,
		isSpaceOverview,
		contentStateState,
		isTitleCenterAligned,
		alignmentButtonState,
		coverPictureState,
		isFirstEmojiPickerOpen,
		isChangingPicture,
		flags,
		isReadOnly,
		isEditorEmpty,
		coverPictureWidth,
		spaceKey,
		contentComponentsDisabled,
		isStatusDisconnected,
		isAIUnsplashImage,
		aiSuggestedUnsplashImages,
		coverImageWidthCached,
		isUnsplash,
		onEmojiPickerClose,
	}: ToolbarAboveTitleProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

		const isLivePage = useIsCurrentPageLive();

		const isServerPlaceholder = useRenderServerPlaceholder(true);

		const isFixedWidth =
			coverImageWidthCached && fg('confluence_frontend_cover_image_ssr') && isServerPlaceholder
				? coverImageWidthCached === COVERPICTUREWIDTH.FIXED
				: coverPictureWidth === COVERPICTUREWIDTH.FIXED;

		const handleStatusButtonOnMouseEnter = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'hovered',
					source: 'statusButton',
					actionSubject: 'pageTitle',
					actionSubjectId: 'addPageStatusButton',
				},
			}).fire();
		}, [createAnalyticsEvent]);

		const isCoverPictureAreaRendered = isUploadingPicture || hasCoverPicture;
		const [isContentStateButtonFocused, setIsContentStateButtonFocused] = useState(false);
		const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState(false);
		const [isAlignmentDropdownOpen, setIsAlignmentDropdownOpen] = useState(false);

		const [isMoreOptionsButtonFocused, setIsMoreOptionsButtonFocused] = useState(false);
		// Tracks if any of the hover buttons are focused (means user tabbed to a hover button)
		const [isHoverButtonFocused, _setIsHoverButtonFocused] = useState<boolean>(
			isAddHeaderImageButtonFocused ||
				isEmojiButtonFocused ||
				isContentStateButtonFocused ||
				isMoreOptionsButtonFocused,
		);

		const onlineShouldShowToolbar =
			(!isFirstEmojiPickerOpen &&
				!isChangingPicture &&
				!fg('confluence_title_toolbar_ttvc_fix') &&
				isTitleHovered) ||
			isHoverButtonFocused ||
			isContentStateMenuOpen ||
			isCoverPictureModalOpen ||
			isAlignmentDropdownOpen;

		const onlineShouldShowHoverButton =
			isTitleHovered ||
			isHoverButtonFocused ||
			isContentStateMenuOpen ||
			isCoverPictureModalOpen ||
			isAlignmentDropdownOpen;

		const [shouldShowToolbarPrevOnlineValue, setShouldShowToolbarPrevOnlineValue] =
			useState<boolean>(onlineShouldShowToolbar);
		const [shouldShowHoverButtonPrevOnlineValue, setShouldShowHoverButtonPrevOnlineValue] =
			useState<boolean>(onlineShouldShowHoverButton);

		useEffect(() => {
			if (!userOffline) {
				setShouldShowToolbarPrevOnlineValue(onlineShouldShowToolbar);
				setShouldShowHoverButtonPrevOnlineValue(onlineShouldShowHoverButton);
			}
		}, [userOffline, onlineShouldShowHoverButton, onlineShouldShowToolbar]);

		const shouldShowToolbar =
			!isReadOnly && (userOffline ? shouldShowToolbarPrevOnlineValue : onlineShouldShowToolbar);
		const shouldShowHoverButton =
			!isReadOnly &&
			(userOffline ? shouldShowHoverButtonPrevOnlineValue : onlineShouldShowHoverButton);

		/* These states are used to track width of emoji button and status button since the width of the button differs with language.
  Due to the absolute positioning, the buttons can overlap if a page does not have a content state or header button.
  These widths are passed to the content state button and more options button so they can space themselves accordingly.
   */
		const [emojiButtonWidth, setEmojiButtonWidth] = useState<number>(0);
		useEffect(() => {
			return () => {
				// Whenever we leave the editor, we need to clean up and reset the content state container values on unmount,
				// otherwise when the user opens editor, previous values are incorrectly persisted (could also do this in breadcrumbs but chose here)
				void contentStateState.resetToDefaults();
				void coverPictureState.resetToDefaults();
				void alignmentButtonState?.resetToDefaults();
			};
		}, [contentStateState, coverPictureState, alignmentButtonState]);

		const editorAppearanceButton = (
			<EditorAppearanceButtonNext
				contentId={contentId}
				disabled={!!contentComponentsDisabled}
				isStatusDisconnected={!!isStatusDisconnected}
			/>
		);

		const emojiButton = (
			<EmojiButton
				contentId={contentId}
				userOffline={userOffline}
				isUnpublishedDraft={isUnpublishedDraft}
				isTitleDisabled={isTitleDisabled}
				shouldShowHoverButton={
					!fg('confluence_title_toolbar_ttvc_fix') ? shouldShowHoverButton : undefined
				}
				isCoverPictureModalOpen={isCoverPictureModalOpen}
				setSelectedEmoji={setSelectedEmoji}
				onEmojiPickerLoaded={onEmojiPickerLoaded}
				onUpdateStartEmoji={onUpdateStartEmoji}
				onUpdateEndEmoji={onUpdateEndEmoji}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				onEmojiPickerClose={onEmojiPickerClose}
			/>
		);

		const { width, leftOffset } = useEditorWidthObserver();
		const coverImageWidth = isFixedWidth ? COVER_IMAGE_FIXED_WIDTH : width;

		const dynamicCoverImageHeight = isFixedWidth
			? COVER_IMAGE_FIXED_HEIGHT
			: getDynamicCoverImageHeight(coverImageWidth);

		const [coverPictureAndButtonWrapperHeight, setCoverPictureAndButtonWrapperHeight] = useState(
			fg('confluence_frontend_cover_image_ssr')
				? dynamicCoverImageHeight
				: MIN_DYNAMIC_COVER_IMAGE_HEIGHT,
		);

		const aiSuggestedImageHeader = (
			<AISuggestedImageHeaderLoading
				isUploadingPicture={isUploadingPicture}
				isAIUnsplashImage={isAIUnsplashImage}
				isFixedWidthImageOption={isFixedWidth}
			/>
		);

		const coverPicAndButton = (
			<CoverPictureAndButton
				contentId={contentId}
				contentType={contentType}
				isPublishingContent={isPublishingContent}
				userOffline={userOffline}
				hasCoverPicture={hasCoverPicture}
				isUploadingPicture={isUploadingPicture}
				shouldShowHoverButton={
					!fg('confluence_title_toolbar_ttvc_fix') ? shouldShowHoverButton : undefined
				}
				hasContentState={hasContentState}
				isUnpublishedDraft={isUnpublishedDraft}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isFixedWidthImageOption={isFixedWidth}
				updateHeight={setCoverPictureAndButtonWrapperHeight}
				currentHeight={coverPictureAndButtonWrapperHeight}
				isReadOnly={isReadOnly}
				spaceKey={spaceKey}
				aiSuggestedUnsplashImages={aiSuggestedUnsplashImages}
				isAIUnsplashImage={isAIUnsplashImage}
				isUnsplash={isUnsplash}
				dynamicCoverImageHeight={dynamicCoverImageHeight}
				width={width}
				coverImageWidth={coverImageWidth}
				leftOffset={leftOffset}
			/>
		);

		const contentStateHoverButton = (
			<ContentStateHoverButton
				contentId={contentId}
				shouldShowHoverButton={
					!fg('confluence_title_toolbar_ttvc_fix') ? shouldShowHoverButton : undefined
				}
				hasContentState={hasContentState}
				contentType={contentType}
				userOffline={userOffline}
				setIsContentStateButtonFocused={setIsContentStateButtonFocused}
				setIsContentStateMenuOpen={setIsContentStateMenuOpen}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
			/>
		);

		// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
		const emojiButtonMeasuredRef = useCallback(
			(node: any) => {
				if (node !== null && !fg('cc_fe_pgxt_skip_unnecessary_emoji_offset_styles')) {
					const width = node.offsetWidth;
					setEmojiButtonWidth(width);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[emojiButton],
		);

		// Putting pubsub subscription in this file since putting it in the contentState component won't work due to re-renders
		// from unmounting and mounting from the breadcrumbs component and contentStateHoverButton component
		const onContentStateUpdated = useCallback(
			(_eventName: any, pubSubData: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!fg('cc_fps_migration_content-state-updated')
				) {
					const newContentState = JSON.parse(
						pubSubData.contentPropertyUpdateRequestBody.value,
					).contentState;
					void contentStateState.setContentState(newContentState);
				}
			},
			[contentStateState],
		);

		const onContentStateUpdatedSubscription = useCallback(
			(contentState: any) => {
				void contentStateState.setContentState(contentState);
			},
			[contentStateState],
		);

		const onContentStateDeleted = useCallback(
			(_eventName: any, _pubSubData: any) => {
				void contentStateState.setContentState(null);
			},
			[contentStateState],
		);

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'updated:contentState',
			onEvent: onContentStateUpdated,
			skip:
				fg('cc_fps_migration_content-state-updated') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'deleted:contentState',
			onEvent: onContentStateDeleted,
		});

		const leftPositionAtEmojiButtonInlineCss =
			isCoverPictureAreaRendered && !fg('cc_fe_pgxt_skip_unnecessary_emoji_offset_styles')
				? // This is a dynamic value so tokens cannot be used here

					{ left: `${emojiButtonWidth}px` }
				: {};

		const coverPictureAndButtonInlineCss =
			isUploadingPicture || hasCoverPicture
				? {
						height: `${coverPictureAndButtonWrapperHeight}px`,
					}
				: {};

		return (
			<Subscribe to={[AlignmentButtonContainer]}>
				{(alignmentButtonContainer: AlignmentButtonContainer) => {
					return (
						<Box
							xcss={[
								toolbarAboveTitleBoxStylesBase,
								isTitleCenterAligned
									? toolbarAboveTitleCenterAlignmentStyles
									: toolbarAboveTitleLeftAlignmentStyles,
								fg('confluence_frontend_title_hover_menu') && toolbarAboveTitleBottomSpacingStyle,
							]}
							testId={
								isTitleCenterAligned
									? 'toolbar-above-title-wrapper-centered'
									: 'toolbar-above-title-wrapper'
							}
						>
							<Box>{aiSuggestedImageHeader}</Box>
							{hasCoverPicture || isUploadingPicture || aiSuggestedUnsplashImages?.[0] ? (
								<Box
									xcss={[
										toolbarStyle,
										(isUploadingPicture || hasCoverPicture) &&
											(fg('confluence_frontend_title_hover_menu')
												? coverPictureAndButtonWithCoverPictureBoxStylesNew
												: coverPictureAndButtonWithCoverPictureBoxStyles),
									]}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={coverPictureAndButtonInlineCss}
									testId="cover-pic-and-button-wrapper"
									data-id="cover-pic-and-button-wrapper"
								>
									{coverPicAndButton}
								</Box>
							) : null}
							{!fg('confluence_title_toolbar_ttvc_fix') && (
								<Box
									xcss={[
										shouldShowToolbar
											? toolbarPlaceholderBoxStylesMarginBottomSpace0
											: fg('live_pages_content_jump_mitigation')
												? contentJumpMitigation_toolbarPlaceholderBoxStylesMarginBottomSpace36px
												: toolbarPlaceholderBoxStylesMarginBottomSpace500,
										// Pushes the toolbar and title areas down for the new hover menu placement
										fg('confluence_frontend_title_hover_menu') && hasCoverPicture
											? hasEmoji
												? toolbarPlaceholderWithEmojiMarginTopStyle
												: toolbarPlaceholderMarginTopStyle
											: undefined,
									]}
									testId="toolbar-placeholder"
								/>
							)}
							<ToolbarButtonsWrapper
								isUploadingPicture={isUploadingPicture}
								hasCoverPicture={hasCoverPicture}
								isTitleCenterAligned={isTitleCenterAligned}
								shouldShowToolbar={shouldShowToolbar}
								hasEmoji={hasEmoji}
								data-testid="toolbar-buttons-wrapper"
							>
								<AlignmentComponent
									setIsMoreOptionsButtonFocused={setIsMoreOptionsButtonFocused}
									alignmentButtonContainer={alignmentButtonContainer}
									floatingToolbarButtonStyle={floatingToolbarButtonStyle}
									userOffline={userOffline}
									VerticalLineWrapper={VerticalLineWrapper}
									VerticalLine={VerticalLine}
									createAnalyticsEvent={createAnalyticsEvent}
									flags={flags}
									contentType={contentType}
									alignmentButtonState={alignmentButtonState}
									coverPictureState={coverPictureState}
									contentId={contentId}
									leftPositionAtEmojiButtonInlineCss={leftPositionAtEmojiButtonInlineCss}
									setIsAlignmentDropdownOpen={setIsAlignmentDropdownOpen}
									isAlignmentDropdownOpen={isAlignmentDropdownOpen}
								/>

								<div ref={emojiButtonMeasuredRef} data-testid="emoji-button-wrapper">
									{emojiButton}
								</div>
								{!hasContentState && !isSpaceOverview && (
									<Box
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={leftPositionAtEmojiButtonInlineCss}
										testId="content-state-button-wrapper"
										onMouseEnter={handleStatusButtonOnMouseEnter}
									>
										{contentStateHoverButton}
									</Box>
								)}
								{hasCoverPicture || isUploadingPicture ? null : (
									<div data-testid="cover-pic-and-button-wrapper">{coverPicAndButton}</div>
								)}
								{isAIEnabledForContent && (
									<SuggestTitleButton
										isEditorEmpty={isEditorEmpty}
										floatingToolbarButtonStyle={floatingToolbarButtonStyle}
									/>
								)}
								{(isSpaceOverview || isLivePage || fg('cc_page_experiences_new_editor_byline')) && (
									<>
										{!fg('confluence_frontend_title_hover_menu') && (
											<VerticalLineWrapper>
												<VerticalLine />
											</VerticalLineWrapper>
										)}
										<Box
											xcss={[
												editorAppearanceButtonBoxXcss,
												isLivePage &&
													fg('confluence_frontend_title_hover_menu') &&
													editorAppearanceButtonLivePageXcss,
												!isLivePage && editorAppearanceButtonClassicPageXcss,
											]}
											testId="editor-appearance-button-wrapper"
										>
											{editorAppearanceButton}
										</Box>
									</>
								)}
								<ContentStateUpdatedSubscription
									contentId={contentId}
									contentType={contentType}
									onDataReceived={onContentStateUpdatedSubscription}
									skip={
										!fg('cc_fps_migration_content-state-updated') ||
										!['DUAL', 'GRAPHQL'].includes(
											StatsigConfigurations.getString(
												'confluence-migrate-fps-to-graphql-subscription',
												'',
											),
										)
									}
								/>
							</ToolbarButtonsWrapper>
						</Box>
					);
				}}
			</Subscribe>
		);
	},
);
